<script setup lang="ts">
import type { RelationshipType } from '../../../../../types/relationship'

import { Range } from '@proj-airi/ui'
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useOnboardingStore } from '../../../../../stores/onboarding'
import { RadioCardDetail } from '../../../../menu'
import { Button } from '../../../../misc'

interface Emits {
  (e: 'next'): void
  (e: 'skip'): void
}

const emit = defineEmits<Emits>()

const { t } = useI18n()
const onboardingStore = useOnboardingStore()
const contentReady = inject<import('vue').Ref<boolean>>('onboarding-content-ready', ref(true))

// ── 关系设置 ──
const affection = ref(onboardingStore.defaultInitialAffection)
const relationType = ref<RelationshipType>(onboardingStore.defaultInitialRelationType)

const relationOptions = [
  { id: 'stranger' as RelationshipType, label: () => t('settings.dialogs.onboarding.relationship.typeStranger'), desc: () => t('settings.dialogs.onboarding.relationship.typeStrangerDesc') },
  { id: 'acquaintance' as RelationshipType, label: () => t('settings.dialogs.onboarding.relationship.typeAcquaintance'), desc: () => t('settings.dialogs.onboarding.relationship.typeAcquaintanceDesc') },
  { id: 'friend' as RelationshipType, label: () => t('settings.dialogs.onboarding.relationship.typeFriend'), desc: () => t('settings.dialogs.onboarding.relationship.typeFriendDesc') },
]

const affectionLabel = computed(() => {
  if (affection.value <= 0)
    return '冷淡'
  if (affection.value <= 20)
    return '一般'
  if (affection.value <= 40)
    return '友善'
  if (affection.value <= 60)
    return '亲近'
  if (affection.value <= 80)
    return '亲密'
  return '挚爱'
})

function syncAffectionToType() {
  if (affection.value < 30)
    relationType.value = 'stranger'
  else if (affection.value < 60)
    relationType.value = 'acquaintance'
  else relationType.value = 'friend'
}

// Range 组件通过 v-model 更新，无原生 @input 事件，故用 watcher 同步关系类型
watch(affection, syncAffectionToType)

function syncTypeToAffection(type: RelationshipType) {
  relationType.value = type
  if (type === 'stranger')
    affection.value = 10
  else if (type === 'acquaintance')
    affection.value = 30
  else if (type === 'friend')
    affection.value = 60
}

function handleNext() {
  onboardingStore.defaultInitialAffection = affection.value
  onboardingStore.defaultInitialRelationType = relationType.value
  emit('next')
}
</script>

<template>
  <div class="min-h-0 flex flex-1 flex-col gap-3">
    <template v-if="contentReady">
      <div
        v-motion
        :initial="{ opacity: 0, y: 16 }"
        :enter="{ opacity: 1, y: 0 }"
        :duration="380"
        class="flex-shrink-0"
      >
        <h2 class="text-xl text-neutral-800 md:text-2xl dark:text-neutral-100">
          {{ t('settings.dialogs.onboarding.relationship.title') }}
        </h2>
        <p class="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {{ t('settings.dialogs.onboarding.relationship.description') }}
        </p>
      </div>

      <div class="flex-1 overflow-y-auto space-y-4">
        <!-- 好感度滑块 -->
        <div
          v-motion
          :initial="{ opacity: 0, scale: 0.97, y: 12 }"
          :enter="{ opacity: 1, scale: 1, y: 0 }"
          :duration="420"
          :delay="130"
        >
          <label class="mb-1 block text-sm text-neutral-700 font-medium dark:text-neutral-300">
            {{ t('settings.dialogs.onboarding.relationship.affectionLabel') }}
            <span class="ml-2 text-neutral-500">{{ affectionLabel }} {{ affection }}</span>
          </label>
          <Range
            v-model="affection"
            :min="0" :max="100" :step="5"
            w-full
            aria-label="好感度"
            :aria-valuetext="affectionLabel"
          />
        </div>

        <!-- 关系类型 -->
        <div
          v-motion
          :initial="{ opacity: 0, scale: 0.96, y: 16 }"
          :enter="{ opacity: 1, scale: 1, y: 0 }"
          :duration="420"
          :delay="240"
        >
          <label class="mb-2 block text-sm text-neutral-700 font-medium dark:text-neutral-300">
            {{ t('settings.dialogs.onboarding.relationship.typeLabel') }}
          </label>
          <div class="grid grid-cols-3 gap-2">
            <RadioCardDetail
              v-for="option in relationOptions"
              :id="option.id"
              :key="option.id"
              v-model="relationType"
              name="relation-type"
              :value="option.id"
              :title="option.label()"
              :description="option.desc()"
              :show-expand-collapse="false"
              @click="syncTypeToAffection(option.id)"
            />
          </div>
        </div>
      </div>

      <div
        v-motion
        :initial="{ opacity: 0 }"
        :enter="{ opacity: 1 }"
        :duration="300"
        :delay="360"
        class="flex gap-2"
      >
        <button
          class="flex-shrink-0 px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
          @click="emit('skip')"
        >
          {{ t('settings.dialogs.onboarding.skipStep') }}
        </button>
        <Button
          class="flex-1"
          variant="primary"
          :label="t('settings.dialogs.onboarding.next')"
          @click="handleNext"
        />
      </div>
    </template>
  </div>
</template>
